import React from 'react';
import loadable from '@loadable/component';

import getPageDataLoadingAPI from '../containers/pageDataLoadingAPI';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';
import PreviewResolverPage from '../containers/PreviewResolverPage/PreviewResolverPage';

// routeConfiguration needs to initialize containers first
// Otherwise, components will import form container eventually and
// at that point css bundling / imports will happen in wrong order.
import { NamedRedirect } from '../components';

const pageDataLoadingAPI = getPageDataLoadingAPI();

const AuthenticationPage = loadable(() => import(/* webpackChunkName: "AuthenticationPage" */ '../containers/AuthenticationPage/AuthenticationPage'));
const CheckoutPage = loadable(() => import(/* webpackChunkName: "CheckoutPage" */ '../containers/CheckoutPage/CheckoutPage'));
const CMSPage = loadable(() => import(/* webpackChunkName: "CMSPage" */ '../containers/CMSPage/CMSPage'));
const AboutPage = loadable(() => import(/* webpackChunkName: "AboutPage" */ '../containers/AboutPage/AboutPage'));
const PresellPage = loadable(() => import(/* webpackChunkName: "PresellPage" */ '../containers/PresellPage/PresellPage'));
// Stable wrapper components for the presell split-test variants. Defined at
// module scope (not inline in the route config) so component identity stays
// stable across renders and loadable's preload() keeps working.
const QuizPage = loadable(() => import(/* webpackChunkName: "QuizPage" */ '../containers/QuizPage/QuizPage'));
const PresellPageRambo = props => <PresellPage {...props} variant="rambo" />;
const PresellPageGrok = props => <PresellPage {...props} variant="grok" />;
const PresellPageFable = props => <PresellPage {...props} variant="fable" />;
const ContactUsPage = loadable(() => import(/* webpackChunkName: "ContactUsPage" */ '../containers/ContactUsPage/ContactUsPage'));
const ContactDetailsPage = loadable(() => import(/* webpackChunkName: "ContactDetailsPage" */ '../containers/ContactDetailsPage/ContactDetailsPage'));
const DashboardPage = loadable(() => import(/* webpackChunkName: "DashboardPage" */ '../containers/DashboardPage/DashboardPage'));
const EditListingPage = loadable(() => import(/* webpackChunkName: "EditListingPage" */ '../containers/EditListingPage/EditListingPage'));
const EmailVerificationPage = loadable(() => import(/* webpackChunkName: "EmailVerificationPage" */ '../containers/EmailVerificationPage/EmailVerificationPage'));
const FAQPage = loadable(() => import(/* webpackChunkName: "FAQPage" */ '../containers/FAQPage/FAQPage'));
const InboxPage = loadable(() => import(/* webpackChunkName: "InboxPage" */ '../containers/InboxPage/InboxPage'));
const LandingPage = loadable(() => import(/* webpackChunkName: "LandingPage" */ '../containers/LandingPage/LandingPage'));
const ListingPageCoverPhoto = loadable(() => import(/* webpackChunkName: "ListingPageCoverPhoto" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCoverPhoto'));
const ListingPageCarousel = loadable(() => import(/* webpackChunkName: "ListingPageCarousel" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCarousel'));
const ManageListingsPage = loadable(() => import(/* webpackChunkName: "ManageListingsPage" */ '../containers/ManageListingsPage/ManageListingsPage'));
const PasswordChangePage = loadable(() => import(/* webpackChunkName: "PasswordChangePage" */ '../containers/PasswordChangePage/PasswordChangePage'));
const PasswordRecoveryPage = loadable(() => import(/* webpackChunkName: "PasswordRecoveryPage" */ '../containers/PasswordRecoveryPage/PasswordRecoveryPage'));
const PasswordResetPage = loadable(() => import(/* webpackChunkName: "PasswordResetPage" */ '../containers/PasswordResetPage/PasswordResetPage'));
const PaymentMethodsPage = loadable(() => import(/* webpackChunkName: "PaymentMethodsPage" */ '../containers/PaymentMethodsPage/PaymentMethodsPage'));
const PrivacyPolicyPage = loadable(() => import(/* webpackChunkName: "PrivacyPolicyPage" */ '../containers/PrivacyPolicyPage/PrivacyPolicyPage'));
const ProfilePage = loadable(() => import(/* webpackChunkName: "ProfilePage" */ '../containers/ProfilePage/ProfilePage'));
const ProfileSettingsPage = loadable(() => import(/* webpackChunkName: "ProfileSettingsPage" */ '../containers/ProfileSettingsPage/ProfileSettingsPage'));
const SearchPageWithMap = loadable(() => import(/* webpackChunkName: "SearchPageWithMap" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithMap'));
const SearchPageWithGrid = loadable(() => import(/* webpackChunkName: "SearchPageWithGrid" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithGrid'));
const StripePayoutPage = loadable(() => import(/* webpackChunkName: "StripePayoutPage" */ '../containers/StripePayoutPage/StripePayoutPage'));
const TermsOfServicePage = loadable(() => import(/* webpackChunkName: "TermsOfServicePage" */ '../containers/TermsOfServicePage/TermsOfServicePage'));
const TransactionPage = loadable(() => import(/* webpackChunkName: "TransactionPage" */ '../containers/TransactionPage/TransactionPage'));
const BlogPage = loadable(() => import(/* webpackChunkName: "BlogPage" */ '../containers/BlogPage/BlogPage'));
const SingleBlogPage = loadable(() => import(/* webpackChunkName: "SingleBlogPage" */ '../containers/SingleBlogPage/SingleBlogPage'));
const NoAccessPage = loadable(() => import(/* webpackChunkName: "NoAccessPage" */ '../containers/NoAccessPage/NoAccessPage'));
const OnboardingPage = loadable(() => import(/* webpackChunkName: "OnboardingPage" */ '../containers/OnboardingPage/OnboardingPage'));
const SubscriptionPage = loadable(() => import(/* webpackChunkName: "SubscriptionPage" */ '../containers/SubscriptionPage/SubscriptionPage'));
const MembershipPage = loadable(() => import(/* webpackChunkName: "MembershipPage" */ '../containers/MembershipPage/MembershipPage'));
const MembershipPausePage = loadable(() => import(/* webpackChunkName: "MembershipPausePage" */ '../containers/MembershipPage/MembershipPausePage'));
const MembershipCancelPage = loadable(() => import(/* webpackChunkName: "MembershipCancelPage" */ '../containers/MembershipPage/MembershipCancelPage'));
const UserDashboardPage = loadable(() => import(/* webpackChunkName: "UserDashboardPage" */ '../containers/UserDashboardPage/UserDashboardPage'));

// Styleguide helps you to review current components and develop new ones
const StyleguidePage = loadable(() => import(/* webpackChunkName: "StyleguidePage" */ '../containers/StyleguidePage/StyleguidePage'));
const SeoCategoriesPage = loadable(() => import(/* webpackChunkName: "SeoCategoriesPage" */ '../containers/SeoCategoriesPage/SeoCategoriesPage'));
const SeoCategoryPage = loadable(() => import(/* webpackChunkName: "SeoCategoryPage" */ '../containers/SeoCategoryPage/SeoCategoryPage'));
const SeoTypePage = loadable(() => import(/* webpackChunkName: "SeoTypePage" */ '../containers/SeoTypePage/SeoTypePage'));
const WorkshopPage = loadable(() => import(/* webpackChunkName: "WorkshopPage" */ '../containers/WorkshopPage/WorkshopPage'));
const WorkshopTypePage = loadable(() => import(/* webpackChunkName: "WorkshopTypePage" */ '../containers/WorkshopPage/WorkshopTypePage'));
const WorkshopProductPage = loadable(() => import(/* webpackChunkName: "WorkshopProductPage" */ '../containers/WorkshopPage/WorkshopProductPage'));
const WorkshopCategoryPage = loadable(() => import(/* webpackChunkName: "WorkshopCategoryPage" */ '../containers/WorkshopPage/WorkshopCategoryPage'));
const StateListsPage = loadable(() => import(/* webpackChunkName: "StateListsPage" */ '../containers/StateListsPage/StateListsPage'));
const ToolsIndexPage = loadable(() => import(/* webpackChunkName: "ToolsIndexPage" */ '../containers/ToolsPage/ToolsIndexPage'));
const TowingCapacityPage = loadable(() => import(/* webpackChunkName: "TowingCapacityPage" */ '../containers/ToolsPage/TowingCapacityPage'));
const TrailerPayloadPage = loadable(() => import(/* webpackChunkName: "TrailerPayloadPage" */ '../containers/ToolsPage/TrailerPayloadPage'));
const SavingsCalculatorPage = loadable(() => import(/* webpackChunkName: "SavingsCalculatorPage" */ '../containers/ToolsPage/SavingsCalculatorPage'));
const ToolboxIndexPage = loadable(() => import(/* webpackChunkName: "ToolboxIndexPage" */ '../containers/ToolboxPage/ToolboxIndexPage'));
const InsurancePage = loadable(() => import(/* webpackChunkName: "InsurancePage" */ '../containers/InsurancePage/InsurancePage'));
const ToolboxRentalAgreementPage = loadable(() => import(/* webpackChunkName: "ToolboxRentalAgreementPage" */ '../containers/ToolboxPage/ToolboxRentalAgreementPage'));
const ToolboxPricingPage = loadable(() => import(/* webpackChunkName: "ToolboxPricingPage" */ '../containers/ToolboxPage/ToolboxPricingPage'));
const ToolboxInspectionPage = loadable(() => import(/* webpackChunkName: "ToolboxInspectionPage" */ '../containers/ToolboxPage/ToolboxInspectionPage'));
const ToolboxPhotosPage = loadable(() => import(/* webpackChunkName: "ToolboxPhotosPage" */ '../containers/ToolboxPage/ToolboxPhotosPage'));
const ToolboxDamagePage = loadable(() => import(/* webpackChunkName: "ToolboxDamagePage" */ '../containers/ToolboxPage/ToolboxDamagePage'));
const ToolboxFirstRentalPage = loadable(() => import(/* webpackChunkName: "ToolboxFirstRentalPage" */ '../containers/ToolboxPage/ToolboxFirstRentalPage'));
const ToolboxLoadSheetsPage = loadable(() => import(/* webpackChunkName: "ToolboxLoadSheetsPage" */ '../containers/ToolboxPage/ToolboxLoadSheetsPage'));
export const ACCOUNT_SETTINGS_PAGES = [
  'ContactDetailsPage',
  'PasswordChangePage',
  'StripePayoutPage',
  'PaymentMethodsPage',
];

// https://en.wikipedia.org/wiki/Universally_unique_identifier#Nil_UUID
export const draftId = '00000000-0000-0000-0000-000000000000';
export const draftSlug = 'draft';

const RedirectToLandingPage = () => <NamedRedirect name="LandingPage" />;

// NOTE: Most server-side endpoints are prefixed with /api. Requests to those
// endpoints are indended to be handled in the server instead of the browser and
// they will not render the application. So remember to avoid routes starting
// with /api and if you encounter clashing routes see server/index.js if there's
// a conflicting route defined there.

// Our routes are exact by default.
// See behaviour from Routes.js where Route is created.
const routeConfiguration = (layoutConfig, accessControlConfig) => {
  const SearchPage = layoutConfig?.searchPage?.variantType === 'map' 
    ? SearchPageWithMap 
    : SearchPageWithGrid;
  const ListingPage = layoutConfig?.listingPage?.variantType === 'carousel' 
    ? ListingPageCarousel 
    : ListingPageCoverPhoto;

  const isPrivateMarketplace = accessControlConfig?.marketplace?.private === true;
  const authForPrivateMarketplace = isPrivateMarketplace ? { auth: true } : {};
  
  return [
    {
      path: '/',
      name: 'LandingPage',
      component: LandingPage,
      loadData: pageDataLoadingAPI.LandingPage.loadData,
    },
    {
      path: '/p/about',
      name: 'AboutPage',
      component: AboutPage,
    },
    // Owner presell / acquisition landing page. Canonical route + 3 visual
    // variants for a Meta-paid split test. All variants render
    // rel=canonical -> /list-your-trailer; non-canonical variants are
    // noindex, so Google only indexes ONE page (zero rotation penalty).
    // Routed BEFORE the CMS /p/:pageId catch-all (these are root-level).
    // Variant sub-paths are declared before the base path as belt-and-
    // suspenders (routes are exact by default in Routes.js, but explicit
    // ordering is safest if that ever changes).
    {
      path: '/list-your-trailer/rambo',
      name: 'PresellPageRambo',
      component: PresellPageRambo,
    },
    {
      path: '/list-your-trailer/grok',
      name: 'PresellPageGrok',
      component: PresellPageGrok,
    },
    {
      path: '/list-your-trailer/fable',
      name: 'PresellPageFable',
      component: PresellPageFable,
    },
    {
      path: '/list-your-trailer',
      name: 'PresellPage',
      component: PresellPage,
    },
    {
      // AI trailer finder + SEO page ("what trailer do I need" intent).
      // Root-level, routed BEFORE the CMS /p/:pageId catch-all.
      path: '/what-trailer-do-i-need',
      name: 'QuizPage',
      component: QuizPage,
    },
    {
      path: '/p/:pageId',
      name: 'CMSPage',
      component: CMSPage,
      loadData: pageDataLoadingAPI.CMSPage.loadData,
    },
    {
      path: '/contact-us',
      name: 'ContactUsPage',
      component: ContactUsPage,
    },
    {
      path: '/s',
      name: 'SearchPage',
      ...authForPrivateMarketplace,
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
    },
    {
      path: '/l',
      name: 'ListingBasePage',
      component: RedirectToLandingPage,
    },
    {
  path: '/l/:state/:city/:slug/:id/view',
  name: 'ListingSEOPage',
  component: ListingPage,
  loadData: pageDataLoadingAPI.ListingPage.loadData,
},
    {
      path: '/l/:slug/:id',
      name: 'ListingPage',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
  {
      path: '/l/:slug/:id/:variant',
      name: 'ListingPageVariant',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/:slug/:city/:state/:id/view',
      name: 'ListingCityStatePage',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/:slug/:state/:id/view',
      name: 'ListingStatePage',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/dashboard/:tab',
      name: 'DashboardPage',
      component: DashboardPage,
      auth: true,
      authPage: 'LoginPage',
      loadData: pageDataLoadingAPI.DashboardPage.loadData,
    },
    {
      path: '/l/claim/:slug/:id',
      name: 'ClaimListingPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/order/checkout',
      name: 'CheckoutPage',
      auth: true,
      component: CheckoutPage,
      setInitialValues: pageDataLoadingAPI.CheckoutPage.setInitialValues,
    },
    {
      path: '/l/:slug/:id/:variant',
      name: 'ListingPageVariant',
      auth: true,
      authPage: 'LoginPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/new',
      name: 'NewListingPage',
      auth: true,
      ownerOnly: true,
      component: () => (
        <NamedRedirect
          name="EditListingPage"
          params={{ slug: draftSlug, id: draftId, type: 'new', tab: 'details' }}
        />
      ),
    },
    {
      path: '/l/:slug/:id/:type/:tab',
      name: 'EditListingPage',
      auth: true,
      ownerOnly: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/:type/:tab/:returnURLType',
      name: 'EditListingStripeOnboardingPage',
      auth: true,
      ownerOnly: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },

    // Canonical path should be after the `/l/new` path since they
    // conflict and `new` is not a valid listing UUID.
    {
      path: '/l/:id',
      name: 'ListingPageCanonical',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/u',
      name: 'ProfileBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/u/:id',
      name: 'ProfilePage',
      ...authForPrivateMarketplace,
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
    },
    {
      path: '/u/:id/:variant',
      name: 'ProfilePageVariant',
      auth: true,
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
    },
    {
      path: '/profile-settings',
      name: 'ProfileSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ProfileSettingsPage,
      // BUGFIX: without this, fetchInquiredListings never runs, so the renter
      // "Trailers you've inquired about" list was permanently empty (the 8/6
      // query fix was correct but dead code without the route-level loadData).
      loadData: pageDataLoadingAPI.ProfileSettingsPage.loadData,
    },

    // Note: authenticating with IdP (e.g. Facebook) expects that /login path exists
    // so that in the error case users can be redirected back to the LoginPage
    // In case you change this, remember to update the route in server/api/auth/loginWithIdp.js
    {
      path: '/login',
      name: 'LoginPage',
      component: AuthenticationPage,
      extraProps: { tab: 'login' },
    },
    {
      path: '/signup',
      name: 'SignupPage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/en/signup',
      name: 'SignupPage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/signup/:userType',
      name: 'SignupForUserTypePage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/confirm',
      name: 'ConfirmPage',
      component: AuthenticationPage,
      extraProps: { tab: 'confirm' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/recover-password',
      name: 'PasswordRecoveryPage',
      component: PasswordRecoveryPage,
    },
    {
      path: '/inbox',
      name: 'InboxBasePage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="InboxPage" params={{ tab: 'sales' }} />,
    },
    {
      path: '/inbox/:tab',
      name: 'InboxPage',
      auth: true,
      authPage: 'LoginPage',
      component: InboxPage,
      loadData: pageDataLoadingAPI.InboxPage.loadData,
    },
    {
      path: '/order/:id',
      name: 'OrderDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'customer' },
      loadData: (params, ...rest) =>
        pageDataLoadingAPI.TransactionPage.loadData({ ...params, transactionRole: 'customer' }, ...rest),
      setInitialValues: pageDataLoadingAPI.TransactionPage.setInitialValues,
    },
    {
      path: '/order/:id/details',
      name: 'OrderDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="OrderDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      path: '/sale/:id',
      name: 'SaleDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'provider' },
      loadData: pageDataLoadingAPI.TransactionPage.loadData,
    },
    {
      path: '/sale/:id/details',
      name: 'SaleDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="SaleDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      path: '/listings',
      name: 'ManageListingsPage',
      auth: true,
      authPage: 'LoginPage',
      ownerOnly: true,
      component: ManageListingsPage,
      loadData: pageDataLoadingAPI.ManageListingsPage.loadData,
    },
    {
      path: '/account',
      name: 'AccountSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="ContactDetailsPage" />,
    },
    {
      path: '/account/contact-details',
      name: 'ContactDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ContactDetailsPage,
      loadData: pageDataLoadingAPI.ContactDetailsPage.loadData,
    },
    {
      path: '/account/change-password',
      name: 'PasswordChangePage',
      auth: true,
      authPage: 'LoginPage',
      component: PasswordChangePage,
    },
    {
      path: '/user-dashboard',
      name: 'UserDashboardPage',
      auth: true,
      authPage: 'UserDashboardPage',
      ownerOnly: true,
      component: UserDashboardPage,
      loadData: pageDataLoadingAPI.UserDashboardPage.loadData,
    },
    {
      path: '/toolbox',
      name: 'ToolboxIndexPage',
      auth: true,
      authPage: 'ToolboxIndexPage',
      ownerOnly: true,
      component: ToolboxIndexPage,
    },
    {
      // Public SEO money page (crawlable, no auth) — the insurance hub the Toolbox card + blog spokes point to.
      path: '/trailer-rental-insurance',
      name: 'InsurancePage',
      component: InsurancePage,
    },
    {
      path: '/toolbox/rental-agreement',
      name: 'ToolboxRentalAgreementPage',
      auth: true,
      authPage: 'ToolboxRentalAgreementPage',
      ownerOnly: true,
      component: ToolboxRentalAgreementPage,
    },
    {
      path: '/toolbox/pricing',
      name: 'ToolboxPricingPage',
      auth: true,
      authPage: 'ToolboxPricingPage',
      ownerOnly: true,
      component: ToolboxPricingPage,
    },
    {
      path: '/toolbox/inspection-checklist',
      name: 'ToolboxInspectionPage',
      auth: true,
      authPage: 'ToolboxInspectionPage',
      ownerOnly: true,
      component: ToolboxInspectionPage,
    },
    {
      path: '/toolbox/photos-that-book',
      name: 'ToolboxPhotosPage',
      auth: true,
      authPage: 'ToolboxPhotosPage',
      ownerOnly: true,
      component: ToolboxPhotosPage,
    },
    {
      path: '/toolbox/damage-and-collections',
      name: 'ToolboxDamagePage',
      auth: true,
      authPage: 'ToolboxDamagePage',
      ownerOnly: true,
      component: ToolboxDamagePage,
    },
    {
      path: '/toolbox/get-your-first-rental',
      name: 'ToolboxFirstRentalPage',
      auth: true,
      authPage: 'ToolboxFirstRentalPage',
      ownerOnly: true,
      component: ToolboxFirstRentalPage,
    },
    {
      path: '/toolbox/load-sheets',
      name: 'ToolboxLoadSheetsPage',
      auth: true,
      authPage: 'ToolboxLoadSheetsPage',
      ownerOnly: true,
      component: ToolboxLoadSheetsPage,
    },
    {
      path: '/account/payments',
      name: 'StripePayoutPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
    },
    {
      path: '/account/payments/:returnURLType',
      name: 'StripePayoutOnboardingPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
    },
    {
      path: '/account/payment-methods',
      name: 'PaymentMethodsPage',
      auth: true,
      authPage: 'LoginPage',
      component: PaymentMethodsPage,
      loadData: pageDataLoadingAPI.PaymentMethodsPage.loadData,
    },
    {
      path: '/terms-of-service',
      name: 'TermsOfServicePage',
      component: TermsOfServicePage,
      loadData: pageDataLoadingAPI.TermsOfServicePage.loadData,
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicyPage',
      component: PrivacyPolicyPage,
      loadData: pageDataLoadingAPI.PrivacyPolicyPage.loadData,
    },
    {
      path: '/styleguide',
      name: 'Styleguide',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/g/:group',
      name: 'StyleguideGroup',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
      {
      path: '/subscription',
      name: 'SubscriptionPage',
      // auth: true,
      component: SubscriptionPage,
      loadData: pageDataLoadingAPI.SubscriptionPage.loadData,

    },
    {
      // Haul Star membership management (pause / resume / cancel / billing).
      // Members-only; non-members are redirected to the upgrade page in-page.
      path: '/account/membership',
      name: 'MembershipPage',
      auth: true,
      authPage: 'LoginPage',
      component: MembershipPage,
    },
    {
      // Dedicated pause-confirm page ("Your off-season, all lined up").
      path: '/account/membership/pause',
      name: 'MembershipPausePage',
      auth: true,
      authPage: 'LoginPage',
      component: MembershipPausePage,
    },
    {
      // Dedicated cancel page ("Cancel Haul Star?" + reason + pause-instead save).
      path: '/account/membership/cancel',
      name: 'MembershipCancelPage',
      auth: true,
      authPage: 'LoginPage',
      component: MembershipCancelPage,
    },
    {
      path: '/styleguide/c/:component',
      name: 'StyleguideComponent',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example',
      name: 'StyleguideComponentExample',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example/raw',
      name: 'StyleguideComponentExampleRaw',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
      extraProps: { raw: true },
    },
    {
      path: '/no-:missingAccessRight',
      name: 'NoAccessPage',
      component: NoAccessPage,
    },
    {
      path: '/notfound',
      name: 'NotFoundPage',
      component: props => <NotFoundPage {...props} />,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /reset-password endpoint
    {
      path: '/reset-password',
      name: 'PasswordResetPage',
      component: PasswordResetPage ,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /verify-email endpoint
    {
      path: '/verify-email',
      name: 'EmailVerificationPage',
      auth: true,
      authPage: 'LoginPage',
      component: EmailVerificationPage,
      loadData: pageDataLoadingAPI.EmailVerificationPage.loadData,
    },
    // Do not change this path!
    //
    // The API expects that the application implements /preview endpoint
    {
      path: '/preview',
      name: 'PreviewResolverPage',
      component: PreviewResolverPage ,
    },   
    {
      // The Workshop — trailer-accessories hub (home / rig picker + gear depts)
      path: '/trailer-accessories',
      name: 'WorkshopPage',
      component: WorkshopPage,
      loadData: pageDataLoadingAPI.WorkshopPage.loadData,
    },
    {
      // Product interstitial — MUST come before the :typeSlug route so 'product'
      // isn't swallowed as a trailer-type slug.
      path: '/trailer-accessories/product/:slug',
      name: 'WorkshopProductPage',
      component: WorkshopProductPage,
      loadData: pageDataLoadingAPI.WorkshopProductPage.loadData,
    },
    {
      // Gear Department (category) page. MUST come before the :typeSlug route so
      // 'category' isn't swallowed as a trailer-type slug.
      path: '/trailer-accessories/category/:catSlug',
      name: 'WorkshopCategoryPage',
      component: WorkshopCategoryPage,
      loadData: pageDataLoadingAPI.WorkshopCategoryPage.loadData,
    },
    {
      // Gear sheet per trailer type (e.g. /trailer-accessories/dump-trailer)
      path: '/trailer-accessories/:typeSlug',
      name: 'WorkshopTypePage',
      component: WorkshopTypePage,
      loadData: pageDataLoadingAPI.WorkshopTypePage.loadData,
    },
    {
      path: '/blog',
      name: 'BlogPage',
      component: BlogPage,
      loadData: pageDataLoadingAPI.BlogPage.loadData,
    },
    {
      path: '/blog/page/:page(\\d+)',
      name: 'BlogPagePaginated',
      component: BlogPage,
      loadData: pageDataLoadingAPI.BlogPage.loadData,
    },
    {
      path: '/faq',
      name: 'FAQPage',
      component: FAQPage,
      // SSR the FAQ Q&A + FAQPage JSON-LD so Google crawls the answers and can
      // render the rich result (was client-fetch-only = invisible to crawlers).
      loadData: pageDataLoadingAPI.FAQPage.loadData,
    },
    {
      path: '/blog/:title',
      name: 'SingleBlogPage',
      component: SingleBlogPage,
      loadData: pageDataLoadingAPI.SingleBlogPage.loadData,
    },
       {
      path: '/states',
      name: 'SeoCategoriesPage',
      component: SeoCategoriesPage ,
    },
   {
      path: '/states/:state',
      name: 'StateListsPage',
      component: StateListsPage,
      loadData: pageDataLoadingAPI.StateListsPage.loadData,
    },
    {
      path: '/states/:state/:title',
      name: 'SeoCategoryPage',
      component: SeoCategoryPage,
      loadData: pageDataLoadingAPI.SeoCategoryPage.loadData,
    },
    {
      // National trailer-TYPE hub pages. Regex-constrained to the known slugs
      // so this never swallows unrelated single-segment URLs. Add a new type
      // to the alternation when its hub is added to util/typeSeoContent.js.
      path: '/:seoTypeSlug(dump-trailer-rental|motorcycle-trailer-rental|car-hauler-rental)',
      name: 'SeoTypePage',
      component: SeoTypePage,
      loadData: pageDataLoadingAPI.SeoTypePage.loadData,
    },
    {
      path: '/onboarding',
      auth: true,
      authPage: 'LoginPage',
      name: 'OnboardingPage',
      component: OnboardingPage ,
    },
    {
      path: '/tools',
      name: 'ToolsIndexPage',
      component: ToolsIndexPage,
    },
    {
      path: '/tools/towing-capacity-calculator',
      name: 'TowingCapacityPage',
      component: TowingCapacityPage,
    },
    {
      path: '/tools/trailer-payload-calculator',
      name: 'TrailerPayloadPage',
      component: TrailerPayloadPage,
    },
    {
      path: '/tools/trailer-rental-income-calculator',
      name: 'SavingsCalculatorPage',
      component: SavingsCalculatorPage,
    },
    
    
  ];
};

export default routeConfiguration;
